// shared/src/prompts/m-contrarian-analyst.ts
// Chain V2 (chain-redesign-v2) — market contrarian path, analyst stage.
// Per design-notes L1/L5/L14/L28/L30.
//
// One of two parallel analyst calls (contrarian + consensus). Each produces a
// full thesis-shaped output. Both drafts are visible to BOTH skeptics
// (cross-fed structural steel-man per L7).
//
// Design boundaries:
//   · NO calibration in prompt (L14 — server-side filter/rank/gate only)
//   · NO output-shape grounding (L1 — grounding is world-state facts only)
//   · NO ticker_universe in prompt (L1 — universe enforcement at translator)
//   · NO sc.* family (L13 — dropped entirely)
//   · NO asym_rank (L12 — dropped)
//   · NO session_outlook (L29 — dropped; consensus path emits its own thesis)
//   · NO pushback (L28 — cross-fed skeptic is structural steel-man at this path)
//   · macro_predictions emitted with the same multi-observable rules as V1

import { z } from 'zod';
import { HorizonEnum, ThesisStatusEnum, HighMedLowEnum, RegimeEnum } from '../resolvers/llm-enum-coerce.js';
import { llmStringItem } from '../resolvers/llm-string-coerce.js';
import { llmNumber } from '../resolvers/llm-number-coerce.js';
import { hoistMisnestedFields } from '../resolvers/hoist-misnested.js';
import { renderWorldStateGrounding, type WorldStateGrounding } from './_grounding.js';
import { renderNewsBlock } from './_news-render.js';
import { renderRssBlock } from './_rss-render.js';
import { renderUpcomingEconBlock } from '../econ-calendar-param.js';
import { renderMacroGroundingBlock, type MacroGroundTruth } from '../translators/macro-grounding.js';
import { formatToolCatalogForPrompt, formatFredSeriesHint } from '../tool-names.js';
import type { EconCalendarEvent } from '../types/econ-calendar.js';
import type { LlmNewsItem } from '../news/types.js';

export const VERSION = '1.12.0';   // 2026-08-17 #23 — request_tools[].args now documented: non-empty args example + explicit news_search query directive + per-tool TOOL ARG SPECS block (worker-injected). Stops the empty-args fail-soft (NEWS_SEARCH_QUERY_MISSING / COMPUTE_INVALID_PRICES). // Prior: 1.11.0 (2026-07-10 — cascade_nodes .min(1)→.max(12).default([]) (omission-safe; empty→[] + EMPTY_CASCADE WARN badge, not a brief kill)). // Prior: 1.10.0 (2026-06-11 — macro-grounding: GROUNDED MACRO ACTUALS block (labeled Core/Headline CPI + code-derived YoY) + no-consensus guard (rule 14b). Stops the 019eb1bf fabrication ("Core CPI 2.9% vs 4.2% consensus"). See docs/macro-grounding-validator-2026-06-11-brief-revision.md. // Prior: 1.9.0 (2026-06-11 — schema rigidity hardening). // Prior: 1.8.0 (2026-06-11 — UPCOMING ECON CALENDAR block). // Prior: 1.7.0 (2026-05-26 #107 — OBSERVABLE = STATE LEVEL + PARTIAL CATALYST FILL ban). Matches m-consensus-analyst.
export const STEP    = 'm.contrarian.analyst';
export const MODEL   = 'deepseek-v4-flash';
// 2026-07-31 — raised 12000 → 24000 after BOTH M-stage analysts truncated and
// killed two consecutive briefs (13:35 + 19:00 UTC). max_tokens covers reasoning
// + content on this model: reasoning drifted from a historical max of 6236 to
// 10509 here (and to a fully-truncated 12000 on the consensus sibling), leaving
// too little budget for the JSON body, which was cut mid-string. 49 prior
// successes had never exceeded 8021 TOTAL, so the old ceiling looked generous
// until the vendor's reasoning length grew. Nothing on our side changed —
// DEEPSEEK_REASONING_EFFORT has been 'low' since 2026-05-25.
// This is a CEILING, not a target: raising it costs nothing on calls that stop
// early, so the headroom is close to free insurance. Model ceiling is 384k.
// Evidence: docs/audits/2026-07-31-chain-truncation/INVESTIGATION.md
// 2026-08-01 — raised 24000 → 32000. The 30-day headroom review found this the
// highest-reasoning step of the whole fleet (peak reasoning 17836, completion
// 19701 = 82% of the 2-day-old 24000 ceiling) — still in the active drift
// regime, so given a pure ceiling costs nothing on early stops, widened the
// margin. docs/audits/2026-08-01-token-cap-headroom-review.md
export const MAX_TOKENS = 32000;
export const ROLE    = 'analyst' as const;
export const DESC    = 'Drafts the contrarian macro thesis — the paradox the desk is missing' as const;   // #45 registry — one-line operator description

// Path-neutral field names per L30.
// macro_predictions stays z.array(z.unknown()) at LLM-emit boundary; per-item
// strict validation runs in market-emit-to-d1 translator (per-prediction
// WARN+drop instead of brief-fatal Zod failure — V1 precedent).
const BaseOutputSchema = z.object({
  thesis_headline:       z.string().min(1),
  thesis_paragraph:      z.string().min(1),
  basis: z.object({
    evidence:            z.string().min(1),
    catalyst:            z.string(),
    invalidation:        z.string(),
  }),
  priced_explanation:    z.string().min(1),       // "<enum>: <rationale>"
  probability:           llmNumber(z.number().min(0).max(100)),    // D38 (2026-05-28): dropped .int(). 2026-06-11: llmNumber-coerced (string "65"/"50%" → number; range stays).
  // Session 5T (2026-05-28) D13: widened to include '2w' — LLM emitted '2w'
  // during first V3 fire (instance 019e6ce5) and Zod parse failed. Per brief
  // revision §12 the time-bucket vocabulary here is informational metadata
  // (signal.event_horizon_at is the authoritative datetime); allowing
  // intermediate buckets matches LLM's natural granularity.
  horizon:               HorizonEnum,
  conditional:           z.string(),
  cascade_prose:         z.string(),
  // Session 5T (2026-05-28) D15: loosened .min(3)→.min(1). Prompt still requests 3-5 typical (normative).
  // 2026-06-23: .max(5)→.max(12). The 5 was a CascadeDiagram display-density spec ("3-5 nodes"), NOT a data
  // cap — the renderer already clamps (CascadeDiagram.resolveCascadeNodes slice 0,5; ZoneThesisRipples chips
  // wrap). A 6-node crash-day cascade hard-failed a whole brief at m.consensus.refine (brief 019ef4b1). 12 is
  // a runaway-junk guard that never trips on real output. See docs/prompt-arbitrary-limits-audit-2026-05-28.md.
  cascade_nodes:         z.array(llmStringItem).max(12).default([]),   // 2026-07-10: omission-safe (see m-consensus-analyst). Empty/omitted → [] + EMPTY_CASCADE WARN badge, not a brief kill.
  cross_portfolio_thread: z.string(),
  top_risk:              z.string(),
  risk_mit:              z.string(),
  thesis_status:         ThesisStatusEnum,
  thesis_div:            HighMedLowEnum,
  regime:                RegimeEnum,
  regime_confidence:     llmNumber(z.number().min(0).max(1)),
  // Session 5T (2026-05-28) D15: loosened .min(2)→.min(1). See macro-prediction-emit.ts:152.
  macro_predictions:     z.array(z.unknown()).min(1).max(5),
  request_tools:         z.array(z.object({
    tool_name: z.string(),
    args:      z.record(z.unknown()),
  })).optional(),
}).passthrough();

// Dropped-middle-brace repair: DeepSeek occasionally omits the closing `}` of
// `basis`, and parseContent's end-append recovery then nests every following
// top-level field inside `basis` — which the non-passthrough `basis` object
// strips, so the required top-level fields read undefined and the brief dies with
// a misleading "N fields required" SchemaError (brief 019fed0c, 2026-08-10). Lift
// any basis-nested top-level field back up BEFORE validation. No-op on clean
// output. docs/investigations/m-contrarian-analyst-basis-nesting-2026-08-10.md
export const OutputSchema = z.preprocess(
  (v) => hoistMisnestedFields(v, 'basis', Object.keys(BaseOutputSchema.shape), { step: STEP }),
  BaseOutputSchema,
);
export type Output = z.infer<typeof OutputSchema>;

export const SYSTEM = `# MARKET CONTRARIAN ANALYST (V2)

## ROLE
Institutional PM, portfolio overlay. Macro → rotation → single name.
You are the CONTRARIAN voice on this brief. Find the paradox the desk is missing — where payload data and consensus disagree — and explain the MECHANISM, not the label.
Every contrarian claim requires: (a) why market hasn't moved yet — inertia is not an answer; (b) a datable catalyst; (c) a falsifier. "Priced in" is a hypothesis to test, not a conclusion.

Your draft will be (i) attacked by a skeptic that ALSO sees the consensus analyst's draft as steel-man counter, (ii) refined by you given that skeptic critique + tool results, and (iii) adjudicated against the consensus path by a synthesis LLM. Stake your strongest contrarian claim — being wrong is fine; being defensive is not.

## CONTEXT YOU RECEIVE (user message)
- GROUNDING FACTS (today, snapshot age) — for forward-date discipline only
- Full macro / crypto payload (VIX, yields, DXY, F&G, BTC dominance, etc.)
- Per-ticker prices for the universe under analysis
- RSS news + macro news

## MACRO INTERPRETATION
Risk regime: hy_spread_regime=STRESS → risk-off all; yield_curve=INVERTED → late-cycle, reduce beta.
Crypto liquidity: m2_yoy_pct rising → \\$BTC/\\$ETH tailwind; fed_balance_chg_B shrinking (QT) → headwind.
Fed expectations: core_pce_yoy_pct>2.5 → hawkish bias; rising 10y breakeven inflation → rate-cut odds down.
Labor: claims↑wk/wk → defensive; unemp at cycle low → late-cycle caution.

## OUTPUT (strict JSON; first char='{'; last char='}'; no commentary outside)
{
  "thesis_headline":    "<≤25w punchy headline contrarian claim vs consensus.>",
  "thesis_paragraph":   "<100-120w. Sentence 1 = headline contrarian call ≤15w. Sentences 2-N = connected mechanism (cite payload field=value, explain WHY the market hasn't moved, close on the trigger). NO 'overall'/'in summary', NO bullet lists.>",
  "basis": {
    "evidence":         "<≤14w: field_a=value vs field_b=value — specific payload contradiction motivating thesis.>",
    "catalyst":         "<≤12w: named event/date that forces repricing. MUST be future (≥ TODAY).>",
    "invalidation":     "<≤12w: one falsifiable condition.>"
  },
  "priced_explanation": "<enum>: <≤25w rationale>. Enum: not_credible | hedged | demand_offset | positioning | spr_release | refiner_margin | timing_lag | other",
  "probability":        50,
  "horizon":            "1w | 1m | 1q",
  "conditional":        "<≤18w: rises to X% if Y.>",
  "cascade_prose":      "<60-80w: explicit A→B→C chain, ≥2 distinct mechanisms.>",
  "cascade_nodes":      ["<≤6w label>", "<≤6w label>", "<≤6w label>"],
  "cross_portfolio_thread": "<60-80w: cross-portfolio mechanism not already in thesis_paragraph or cascade.>",
  "top_risk":           "<≤25w: trigger → transmission → affected positions.>",
  "risk_mit":           "<≤25w: specific hedge levels + triggers.>",
  "thesis_status":      "IN_PLAY | WATCH | INVALIDATED",
  "thesis_div":         "HIGH | MED | LOW",
  "regime":             "risk-on | risk-off | mixed | transitioning",
  "regime_confidence":  0.65,
  "request_tools":      [{ "tool_name": "<name>", "args": { "<arg>": "<value>" } }],
  "macro_predictions":  [
    {
      "prediction_mode":   "trend",
      "thesis_narrative":  "<≤180w narrative tying mechanism to predicted observable moves>",
      "horizon_days":      14,
      "horizon_ci_low_days":  7,
      "horizon_ci_high_days": 21,
      "predicted_observables": [
        {
          "indicator":     "$BTC",
          "baseline_value": 105000,
          "direction":     "up",
          "change_pct":    5.0,
          "change_ci_low": 2.0,
          "change_ci_high": 8.0,
          "is_headline":   true
        }
      ]
    }
  ]
}

## RULES (strict)
1. First char='{'. Last char='}'. No text outside JSON.
2. No " inside strings. No newlines inside string values.
3. basis.evidence: payload field=value vs field=value. NO fabricated data.
4. basis.catalyst: concrete + future-dated (≥ TODAY from GROUNDING). News / RSS / confirmed macro. "Market could recover" is INVALID.
5. PARADOX CHECK. The thesis REQUIRES a mechanism for why the market hasn't moved. priced_explanation MUST be from the enum, format "<enum>: <rationale>". "Priced in" without a named mechanism = INVALID — drop the claim. If no defensible contrarian exists, set thesis_headline = "" and emit a wide-CI trend prediction set.
6. thesis_status: IN_PLAY = active + not triggered; WATCH = invalidation approaching; INVALIDATED = market proved wrong.
7. thesis_div: HIGH = strong disagreement w/ consensus; MED = partial; LOW = lean.
8. regime: "mixed" when truly uncertain. Do NOT anchor at "transitioning" for every ambiguous state.
9. regime_confidence (0-1, two decimals): score conviction in the REGIME label itself — NOT in your thesis. ≥0.75 when ≥3 macro signals agree. 0.40-0.74 when 1-2 signals conflict. ≤0.39 when no clear majority. Emit 0.50 when the signals genuinely balance — anti-hedge rule removed (Session 5T D36).
9a. probability: PERCENT (0-100) conviction in the thesis. 50 = coin-flip; 80 = high conviction; 20 = low. NOT a 0-1 decimal. Decimals OK (67.5 valid). D38 (Session 5T) dropped the int-only constraint.
10. CATALYST EVENT VERIFICATION. For every entry in macro_predictions[] with prediction_mode='catalyst', emit one news_search request in request_tools[] targeting that event — put the event in the query arg: {"tool_name":"news_search","args":{"query":"<the event as search keywords>"}}. GENERAL: populate request_tools[].args for EVERY tool you request per the TOOL ARG SPECS block (each tool's args differ — news_search takes query, correlation takes prices+days, etc.); emit args:{} ONLY for a tool the specs mark "(no args)". The downstream refine pass verifies event date and consensus framing from the tool results.
11. HEADLINE OBSERVABLES. Across all predictions in macro_predictions[], mark exactly 3-5 observables as \`is_headline: true\` — the observables MOST DIAGNOSTIC for distinguishing your contrarian world from the consensus world (where consensus and you most disagree on direction or magnitude). Fewer/more triggers a code fallback (top-N by |change_pct|) — keep editorial control by marking your own.
12. DATE TENSE DISCIPLINE. The structurally-forward fields — basis.catalyst, basis.invalidation, conditional — MUST reference dates ≥ TODAY (GROUNDING FACTS). If no forward event exists, drop the claim (emit ""). NEVER anchor a past event as if forthcoming.
13. NUMERIC GROUNDING. Numbers presented as CURRENT STATE MUST match payload values within rounding. Projected levels may exceed payload scope but MUST be plausible against ticker_data ranges.
13b. MACRO ACTUALS + NO CONSENSUS. For any macro print (CPI, PCE, jobs, GDP, rates), use ONLY the figures in the GROUNDED MACRO ACTUALS block (and payload) — do NOT recall macro numbers from memory. Core vs Headline CPI are DISTINCT (the block labels them); never conflate. NO analyst consensus/estimate/forecast data is provided to you — therefore NEVER state a print 'vs consensus'/'vs forecast'/'beat'/'missed'/'in line'/'surprise' or cite a forecast/estimate number. Describe the actual, its level, and its trend only.
14. PROSE FORMAT. Narrative fields (thesis_paragraph, cascade_prose, cross_portfolio_thread): lead with the punchline (≤15w sentence 1), expand in connected prose. Each sentence builds on the previous: cause → effect → action.

## MACRO PREDICTIONS
- 2-5 entries required. Each: prediction_mode='catalyst' (named dated event) OR 'trend' (ambient regime).
- CATALYST mode is a CONTRACT: when you tag prediction_mode='catalyst' you MUST populate ALL 7 catalyst fields — catalyst_description (≤50w), catalyst_source (e.g. "fed_calendar" / "RSS" / "earnings_calendar"), predicted_catalyst_date (ISO YYYY-MM-DD ≥ TODAY), catalyst_date_ci_low_days (int, can be negative), catalyst_date_ci_high_days (int), catalyst_probability (number 0-1, NEVER 0.5 as hedge), post_catalyst_window_days (int ≥0). AND set horizon_days / horizon_ci_low_days / horizon_ci_high_days = null. Missing ANY catalyst field while tagged 'catalyst' triggers SCHEMA_REJECT — the entire prediction is dropped. If you can't name the event with all 7 fields, use trend mode instead. PARTIAL FILLS (5 of 7 catalyst fields set, 2 missing) ALSO drop — the writer does NOT fabricate missing catalyst_probability / post_catalyst_window_days values. Set ALL 7 or set NONE and use trend.
- OBSERVABLE = STATE LEVEL (NOT FLOW). \`baseline_value\` MUST be a positive scalar representing the indicator's CURRENT LEVEL (price, yield, spread, index value). FORBIDDEN as observables: weekly flow metrics (crypto_etf_weekly_flow, ETF net flow, fund flow, AUM change), period-over-period deltas, count-of-events. If flow supports the thesis, put the argument in basis.evidence / thesis_paragraph — NOT as a predicted_observable. Zod rejects baseline_value ≤ 0 and the prediction drops.
- TREND mode is the dual contract: prediction_mode='trend' MUST populate horizon_days (positive int), horizon_ci_low_days (int), horizon_ci_high_days (int), AND set ALL 7 catalyst_* fields = null.
- MODE FAITHFULNESS rule: if you provided horizon_days and ZERO catalyst_* fields, the prediction is trend — emit prediction_mode='trend' verbatim, do not tag 'catalyst' aspirationally. Mis-tagging is the most common drop reason in this stage.
- predicted_observables: ≥3 entries spanning ≥2 distinct asset classes, of {equity, rates, FX, commodity, crypto} — e.g. SPY(equity)+TNX(rates)+GLD(commodity) already spans 3. A genuine NON-crypto cross-asset set qualifies; do NOT bolt on a crypto observable just to satisfy the rule. Buckets: crypto=\`$\`-prefix ($BTC/$ETH); rates=TNX/TYX/TLT/IEF; FX=DXY/EURUSD; commodity=GLD/USO/CPER/PDBC; equity=SPY/QQQ/sector-ETFs/stocks/VIX.
- Each observable: indicator (Yahoo ticker — $-prefix for crypto, bare uppercase elsewhere; e.g. SPY/QQQ/IWM/NVDA/TNX/TYX/TLT/IEF/HYG/LQD/GLD/USO/CPER/PDBC/$BTC/$ETH/VIX/DXY/EURUSD). Avoid FRED IDs (DGS10/FEDFUNDS/T10YIE/etc.) — the universe validator probes Yahoo and rejects FRED-only series. The writer-boundary resolver auto-translates "gold"→GLD/"oil"→USO/"10y_yield"→TNX/etc., but emitting Yahoo canonical directly is preferred. baseline_value (from payload), direction, signed change_pct, change_ci_low, change_ci_high.
- baseline_value MUST be the indicator's CURRENT price from payload (>0). If payload does not carry the indicator, DROP the observable entirely — do not emit baseline_value=0 as a placeholder. Zod rejects 0 at boundary; the chain fails-loud on misshapen emit.

## TOOL CATALOG (request_tools[], max 8 entries — REQUIRED: one news_search per catalyst-mode prediction)
${formatToolCatalogForPrompt()}
${formatFredSeriesHint()}

Tool names are CASE-SENSITIVE and EXACT. Do not pluralize, abbreviate, alias, or guess — names not in this catalog return error:'unknown_tool' and waste tokens. Pick from the catalog above OR proceed without the tool.

Tool requests feed the refinement pass; they do NOT change this analyst output (locked at emit time).
`;

export interface MContrarianAnalystInput {
  readonly macro:     Record<string, unknown>;
  readonly prices:    Record<string, Record<string, unknown>>;
  readonly sentiment: Record<string, unknown>;
  readonly tickers:   readonly string[];
  readonly grounding?: WorldStateGrounding;
  readonly news?:     ReadonlyArray<LlmNewsItem>;
  /** 2026-06-11 — operator-curated forward econ calendar (next ~12w). Anchors
   *  basis.catalyst dates to real upcoming events. Absent → no block. */
  readonly upcoming_events?: ReadonlyArray<EconCalendarEvent>;
  /** 2026-06-11 — FRED-derived grounded macro actuals (labeled Core/Headline
   *  CPI + code-computed YoY). Authoritative; the LLM must not recall macro
   *  numbers from memory. Absent/empty → no block. */
  readonly macro_grounding?: ReadonlyArray<MacroGroundTruth>;
}

function fmt(v: unknown): string {
  if (v == null) return 'not set';
  if (typeof v === 'string') return v.length === 0 ? 'not set' : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

export function buildUserPrompt(input: MContrarianAnalystInput): string {
  const { macro, prices, sentiment, tickers, grounding } = input;
  const groundingBlock = grounding ? renderWorldStateGrounding(grounding) + '\n' : '';

  const macroLines = Object.entries(macro)
    .filter(([k]) => typeof k === 'string' && !k.startsWith('_'))
    .map(([k, v]) => `  ${k}: ${fmt(v)}`)
    .join('\n');

  const priceLines = tickers.map((t) => {
    const p = prices[t] ?? {};
    const entries = Object.entries(p).map(([k, v]) => `${k}=${fmt(v)}`).join(', ');
    return `  ${t}: ${entries || 'no data'}`;
  }).join('\n');

  const polyEntries = sentiment['polymarket'] ?? {};
  // RSS via the shared per-item renderer (title + date + summary, bounded).
  // Replaces the pre-2026-07-08 JSON.stringify(rss).slice(0,2000) that rendered
  // titles-only; computed.rss now carries {title,summary,date} per item. See
  // _rss-render.ts.
  const rssLine     = renderRssBlock(sentiment['rss']);
  const polyLine    = Object.keys(polyEntries as object).length > 0
    ? JSON.stringify(polyEntries).slice(0, 1000) : 'no polymarket data';

  const upcomingBlock = input.upcoming_events && input.upcoming_events.length > 0
    ? renderUpcomingEconBlock(input.upcoming_events) + '\n'
    : '';

  const macroGroundingBlock = input.macro_grounding && input.macro_grounding.length > 0
    ? renderMacroGroundingBlock(input.macro_grounding) + '\n'
    : '';

  return `${groundingBlock}${macroGroundingBlock}TICKERS: ${tickers.join(', ')}

MACRO / CRYPTO PAYLOAD:
${macroLines || '  (empty)'}

${upcomingBlock}PER-TICKER PRICES:
${priceLines || '  (empty)'}

RSS NEWS:
${rssLine}

POLYMARKET:
  ${polyLine}

MACRO NEWS (last 48h):
${renderNewsBlock(input.news ?? [])}

REQUEST: emit_market_contrarian_analyst

Emit a SINGLE JSON object exactly matching the schema. Apply PARADOX CHECK + PROSE FORMAT rules. macro_predictions (2-5 entries, ≥3 observables, ≥2 asset classes per entry) MANDATORY. If macro/prices are sparse, emit thesis_headline = "" rather than fabricate, but still emit at least 2 trend-mode predictions with wide CIs.`;
}
